import { type EnvironmentProviders, makeEnvironmentProviders, type Provider } from '@angular/core';

import { of } from 'rxjs';

import { APP_CONFIG, AUTH_METHOD, AUTH_TOKEN } from '@core/common-legacy';
import { provideTellentConversationStartersContext } from '@core/tellent-assistant-contracts';
import {
  provideConversationStartersNgrx,
  provideTellentAssistantActorGetter,
  provideTellentAssistantAuthTokenGetter,
  provideTellentAssistantCurrentThreadNgrx,
  provideTellentAssistantEndpoint,
  provideTellentAssistantResourceIdGetter,
  provideTellentAssistantThreadsBaseUrl,
  provideTellentAssistantThreadsNgrx,
} from '@core/tellent-assistant-data';
import { type NavigateRoute, provideTellentNavigateTool } from '@core/tellent-assistant-logic';
import {
  provideTellentApprovalInterruptRenderer,
  provideTellentChoiceInterruptRenderer,
  provideTellentTextInputInterruptRenderer,
  provideTellentToolApprovalInterruptRenderer,
} from '@core/tellent-assistant-ui';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { ORG_GUID } from '@core/tellent-types';

import { provideTellentAssistant } from './tellent-assistant.providers';

interface TellentAssistantHostAppConfig {
  readonly tellentApiUrl: string;
}

export interface TellentAssistantHostOptions {
  readonly navigateRoutes: readonly NavigateRoute[];
}

export function provideTellentAssistantHost(
  options: TellentAssistantHostOptions,
): EnvironmentProviders {
  return makeEnvironmentProviders([
    ...buildTellentAssistantHostNonNgrxProviders(options),
    ...provideTellentAssistantThreadsNgrx,
    ...provideTellentAssistantCurrentThreadNgrx,
    ...provideConversationStartersNgrx,
  ]);
}

/**
 * Non-NgRx providers shared by the standalone helper and the legacy NgModule
 * bridge (`TellentAssistantHostModule`). NgRx registration differs between
 * the two: standalone uses `provideState`, legacy uses `StoreModule.forFeature`.
 */
export function buildTellentAssistantHostNonNgrxProviders(
  options: TellentAssistantHostOptions,
): Array<Provider | EnvironmentProviders> {
  return [
    provideTellentAssistant({ endpoint: '', authTokenGetter: () => null }),
    <Provider>provideTellentAssistantEndpoint({
      useFactory: resolveEndpoint,
      deps: [APP_CONFIG, ORG_GUID],
    }),
    <Provider>provideTellentAssistantThreadsBaseUrl({
      useFactory: resolveBaseUrl,
      deps: [APP_CONFIG, ORG_GUID],
    }),
    <Provider>provideTellentAssistantAuthTokenGetter({
      useFactory: resolveAuthTokenGetter,
      deps: [AUTH_TOKEN, AUTH_METHOD],
    }),
    <Provider>provideTellentAssistantResourceIdGetter({
      useFactory: resolveResourceIdGetter,
      deps: [TellentCurrentUserDataFacade],
    }),
    <Provider>provideTellentAssistantActorGetter({
      useFactory: resolveActorGetter,
      deps: [ORG_GUID, TellentCurrentUserDataFacade],
    }),
    provideTellentNavigateTool({ allowedRoutes: [...options.navigateRoutes] }),
    <Provider>provideTellentConversationStartersContext({ useValue: of(null) }),
    provideTellentApprovalInterruptRenderer(),
    provideTellentChoiceInterruptRenderer(),
    provideTellentTextInputInterruptRenderer(),
    provideTellentToolApprovalInterruptRenderer(),
  ];
}

function resolveEndpoint(appConfig: TellentAssistantHostAppConfig, orgGuid: string | null): string {
  return `${resolveBaseUrl(appConfig, orgGuid)}/agui/hrAssistant/stream`;
}

function resolveBaseUrl(appConfig: TellentAssistantHostAppConfig, orgGuid: string | null): string {
  const isDev = true;
  return isDev
    ? 'http://localhost:4111'
    : `${appConfig.tellentApiUrl}/intelligence/org/${orgGuid}/assistant/proxy`;
}

function resolveAuthTokenGetter(token: string, method: string): () => string | null {
  return () => (token ? `${method} ${token}` : null);
}

function resolveResourceIdGetter(
  currentUserFacade: TellentCurrentUserDataFacade,
): () => string | null {
  return () => currentUserFacade.currentUserGuid() ?? null;
}

function resolveActorGetter(
  orgGuid: string | null,
  currentUserFacade: TellentCurrentUserDataFacade,
): () => string | null {
  return () => {
    const user = currentUserFacade.currentUserGuid();
    const member = currentUserFacade.organizationMembership()?.guid;
    if (!orgGuid || !user || !member) return null;
    return JSON.stringify({
      type: 'account',
      data: { org: orgGuid, user, member },
      features: ['ask', 'wiki', 'hr_assistant', 'hitl_test'],
    });
  };
}
